import React, { useState, useRef, useEffect } from 'react'
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom'
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  Divider,
  CircularProgress,
} from '@mui/material'
import { Turnstile } from '@marsidev/react-turnstile'
import { useAuth } from '@/hooks/useAuth'
import { useTranslation } from 'react-i18next'

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '1x00000000000000000000AA'
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || ''

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: object) => void
          renderButton: (element: HTMLElement, config: object) => void
        }
      }
    }
  }
}

export const LoginForm: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { login, loginWithGoogle } = useAuth()
  const { t } = useTranslation()

  const [formData, setFormData] = useState({ email: '', password: '' })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const turnstileRef = useRef<any>(null)
  const googleButtonRef = useRef<HTMLDivElement>(null)

  const from = location.state?.from?.pathname || '/dashboard'

  const handleGoogleCredential = async (response: { credential: string }) => {
    setLoading(true)
    setError('')
    try {
      await loginWithGoogle(response.credential)
      navigate(from, { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.login.googleError'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return

    const initGoogle = () => {
      if (!window.google || !googleButtonRef.current) return
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleCredential,
        auto_select: false,
        cancel_on_tap_outside: true,
      })
      window.google.accounts.id.renderButton(googleButtonRef.current as HTMLElement, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        width: (googleButtonRef.current as HTMLElement).offsetWidth || 400,
        text: 'signin_with',
        logo_alignment: 'left',
      })
    }

    const scriptId = 'google-gis-script'
    if (document.getElementById(scriptId)) {
      initGoogle()
      return
    }

    const script = document.createElement('script')
    script.id = scriptId
    script.src = 'https://accounts.google.com/gsi/client'
    script.async = true
    script.defer = true
    script.onload = initGoogle
    document.head.appendChild(script)
  }, [])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
    setError('')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    if (!turnstileToken) {
      setError(t('auth.login.completeCaptcha'))
      setLoading(false)
      return
    }

    try {
      await login(formData.email, formData.password, turnstileToken)
      navigate(from, { replace: true })
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('auth.login.error')
      const errorMap: Record<string, string> = {
        'Invalid credentials': t('auth.login.invalidCredentials'),
        'Account not activated or password not set. Please check your email.': t('auth.login.notActivated'),
        'Security verification is required': t('auth.login.completeCaptcha'),
        'Security verification failed. Please try again.': t('auth.login.captchaFailed'),
      }
      setError(errorMap[errorMessage] || errorMessage)
      setTurnstileToken(null)
      turnstileRef.current?.reset()
    } finally {
      setLoading(false)
    }
  }

  return (
    <Container component="main" maxWidth="sm">
      <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Link component={RouterLink} to="/" underline="hover" sx={{ fontWeight: 700, fontSize: '1.2rem', color: 'text.primary', mb: 3, alignSelf: 'flex-start' }}>
          ← Orelytics
        </Link>
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Typography component="h1" variant="h4" align="center" gutterBottom>
            {t('auth.login.title')}
          </Typography>
          <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 3 }}>
            {t('auth.login.subtitle')}
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label={t('auth.fields.email')}
              name="email"
              autoComplete="email"
              autoFocus
              value={formData.email}
              onChange={handleChange}
              disabled={loading}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label={t('auth.fields.password')}
              type="password"
              id="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleChange}
              disabled={loading}
            />

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Turnstile
                ref={turnstileRef}
                siteKey={TURNSTILE_SITE_KEY}
                onSuccess={(token) => setTurnstileToken(token)}
                onError={() => setTurnstileToken(null)}
                onExpire={() => setTurnstileToken(null)}
              />
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading || !turnstileToken}
            >
              {loading ? <CircularProgress size={24} /> : t('auth.login.submit')}
            </Button>

            {GOOGLE_CLIENT_ID && (
              <>
                <Divider sx={{ my: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('auth.or')}
                  </Typography>
                </Divider>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <Box ref={googleButtonRef} sx={{ width: '100%' }} />
                </Box>
              </>
            )}

            <Box sx={{ textAlign: 'center' }}>
              <Link component={RouterLink} to="/forgot-password" variant="body2" sx={{ display: 'block', mb: 1 }}>
                {t('auth.login.forgotPassword')}
              </Link>
              <Link component={RouterLink} to="/register" variant="body2">
                {t('auth.login.noAccount')}
              </Link>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  )
}
